import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, Select, Input, Button, message, Modal, List, Tag, Popconfirm } from 'antd';
import { PlayCircleOutlined, HistoryOutlined, EyeOutlined, DeleteOutlined, ArrowLeftOutlined } from '@ant-design/icons';
import axios from 'axios';
import { v4 as uuidv4 } from 'uuid';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import { ApiResponse, WorkflowMarketHistory } from '../types';

const { TextArea } = Input;
const { Option } = Select;

interface SizeOption {
  label: string;
  width: number;
  height: number;
}

interface WorkflowInfo {
  market_id: number;
  wf_id: string;
  display_name: string;
  description: string;
  route_slug: string;
  api_params_template: string;
  workflow_name: string;
}

// 使用全局定义的 WorkflowMarketHistory 类型，不需要重复定义

interface TaskDetail {
  task_id: string;
  task_name?: string;
  status: string;
  created_at: string;
  input_params?: string; // API的JSON格式参数
  ui_params?: string; // 界面输入参数字符串
  result_info?: any;
}

interface WorkflowOption {
  wf_id: string;
  wf_name: string;
  wf_type: string;
}

const BeautySingImgPage: React.FC = () => {
  // 硬编码 routeSlug 为 beauty-sing-image
  const routeSlug = 'beauty-sing-image';
  const navigate = useNavigate();
  const { user, isLoggedIn, checkTaskPermission } = useAuth();
  
  const [workflowInfo, setWorkflowInfo] = useState<WorkflowInfo | null>(null);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  
  // 工作流选择状态
  const [workflowOptions, setWorkflowOptions] = useState<WorkflowOption[]>([]);
  const [selectedWorkflow, setSelectedWorkflow] = useState<WorkflowOption | null>(null);
  const [workflowOptionsLoading, setWorkflowOptionsLoading] = useState(false);
  
  // 表单状态
  const [selectedSize, setSelectedSize] = useState<SizeOption>({
    label: '9:16（540 × 960）',
    width: 540,
    height: 960
  });
  const [prompt, setPrompt] = useState('');
  const [imageCount, setImageCount] = useState(1);
  
  // 历史记录状态
  const [taskHistory, setTaskHistory] = useState<WorkflowMarketHistory[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  
  // 任务详情弹窗状态
  const [taskDetailVisible, setTaskDetailVisible] = useState(false);
  const [taskDetail, setTaskDetail] = useState<TaskDetail | null>(null);
  const [taskDetailLoading, setTaskDetailLoading] = useState(false);

  const sizeOptions: SizeOption[] = [
    { label: '9:16（540 × 960）', width: 540, height: 960 },
    { label: '16:9（960 × 540）', width: 960, height: 540 },
    { label: '3:4（480 × 640）', width: 480, height: 640 },
    { label: '4:3（640 × 480）', width: 640, height: 480 },
    { label: '1:1（480 × 480）', width: 480, height: 480 },
  ];

  // 获取工作流信息
  useEffect(() => {
    const fetchWorkflowInfo = async () => {
      if (!routeSlug) return;
      
      setLoading(true);
      try {
        const response = await axios.get(`${import.meta.env.VITE_API_BASE_URL}/api/workflow/${routeSlug}`);
        if (response.data.success) {
          setWorkflowInfo(response.data.data);
          // 获取工作流信息成功后，获取工作流选项
          await fetchWorkflowOptions(response.data.data.wf_id);
        } else {
          message.error(response.data.message || '获取工作流信息失败');
        }
      } catch (error) {
        console.error('获取工作流信息失败:', error);
        message.error('获取工作流信息失败');
      } finally {
        setLoading(false);
      }
    };

    fetchWorkflowInfo();
  }, [routeSlug]);

  // 获取工作流选项
  const fetchWorkflowOptions = async (wfIds: string) => {
    if (!wfIds) return;
    
    setWorkflowOptionsLoading(true);
    try {
      const response = await axios.get(`${import.meta.env.VITE_API_BASE_URL}/api/workflow-options?wf_ids=${wfIds}`);
      if (response.data.success && response.data.data) {
        // 确保返回的数据是数组
        const options = Array.isArray(response.data.data) ? response.data.data : [];
        setWorkflowOptions(options);
        // 默认选择第一个工作流
        if (options.length > 0) {
          setSelectedWorkflow(options[0]);
        }
      } else {
        console.error('获取工作流选项失败:', response.data.message);
        setWorkflowOptions([]); // 设置为空数组
        message.error('获取工作流选项失败');
      }
    } catch (error) {
      console.error('获取工作流选项失败:', error);
      setWorkflowOptions([]); // 设置为空数组
      message.error('获取工作流选项失败');
    } finally {
      setWorkflowOptionsLoading(false);
    }
  };

  // 获取任务历史
  const fetchTaskHistory = async () => {
    if (!workflowInfo?.market_id) return;
    
    setHistoryLoading(true);
    try {
      // 使用api实例而不是fetch，并使用实际用户ID
      const response: ApiResponse<{ histories: WorkflowMarketHistory[] }> = await api.get(`/workflow-histories/market/${workflowInfo.market_id}/user/${user?.user_id || 'anonymous'}`);
      if (response.success && response.data?.histories) {
        setTaskHistory(response.data.histories);
      } else {
        console.error('获取任务历史失败:', response.message);
      }
    } catch (error) {
      console.error('获取任务历史失败:', error);
    } finally {
      setHistoryLoading(false);
    }
  };

  useEffect(() => {
    fetchTaskHistory();
  }, [workflowInfo?.market_id]);

  // 处理参数模板替换
  const processApiParams = (template: string) => {
    try {
      // 检查template是否为空或null
      if (!template || template.trim() === '') {
        console.warn('参数模板为空');
        return [];
      }
      
      // 预处理模板，确保所有值都被引号包裹
      let processedTemplate = template;
      // 匹配形如 "value":{xxx} 的模式，将其转换为 "value":"{xxx}"
      processedTemplate = processedTemplate.replace(/"value"\s*:\s*\{([^{}]+)\}/g, '"value":"{$1}"');
      
      const params = JSON.parse(processedTemplate);
      
      // 确保params是数组
      if (!Array.isArray(params)) {
        console.error('参数模板不是数组格式:', params);
        return [];
      }
      
      return params.map((param: any) => ({
        ...param,
        value: typeof param.value === 'string' ? param.value
          .replace('{prompt}', prompt)
          .replace('{width}', selectedSize.width.toString())
          .replace('{height}', selectedSize.height.toString())
          .replace('{count}', imageCount.toString()) : param.value
      }));
    } catch (error) {
      console.error('解析参数模板失败:', error);
      console.error('模板内容:', template);
      return [];
    }
  };

  // 提交任务
  const handleSubmit = async () => {
    // 检查用户是否登录
    if (!isLoggedIn || !user) {
      message.warning('请先登录后再执行工作流');
      navigate('/login');
      return;
    }

    // 检查任务创建权限
    const permissionResult = await checkTaskPermission();
    if (!permissionResult.canExecute) {
      message.warning(permissionResult.message);
      return;
    }

    if (!prompt.trim()) {
      message.error('请输入图片提示词');
      return;
    }

    if (!selectedWorkflow) {
      message.error('请选择一个工作流');
      return;
    }

    if (!workflowInfo) {
      message.error('工作流信息未加载');
      return;
    }

    // 显示确认框
    Modal.confirm({
      title: '确认提交任务',
      content: '点击确定后，任务将进入生成队列。',
      okText: '确定',
      cancelText: '取消',
      onOk: async () => {
        await executeSubmit();
      }
    });
  };

  // 执行提交任务的逻辑
  const executeSubmit = async () => {
    setSubmitting(true);
    try {
      // 再次检查 workflowInfo 是否存在
      if (!workflowInfo) {
        message.error('工作流信息未加载');
        return;
      }
      
      const processedParams = processApiParams(workflowInfo.api_params_template);
      
      // 生成task_name（display_name + 时间戳）
      const now = new Date();
      const timeString = now.getFullYear().toString() + 
                        (now.getMonth() + 1).toString().padStart(2, '0') + 
                        now.getDate().toString().padStart(2, '0') + 
                        now.getHours().toString().padStart(2, '0') + 
                        now.getMinutes().toString().padStart(2, '0') + 
                        now.getSeconds().toString().padStart(2, '0');
      const task_name = `${workflowInfo.display_name}_${timeString}`;
      
      // 生成task_id
      const timestamp = Math.floor(Date.now());
      const randomPart = uuidv4().substring(0, 8);
      const task_id = `sub_${timestamp}_${randomPart}`;
      
      // 构建参数字符串，格式：工作流：测试工作流3|尺寸选择640x480|图片提示词：a dog|图片数量：3张
      const cleanPrompt = prompt.replace(/\|/g, ''); // 移除提示词中的|符号
      const uiParamsString = `工作流：${selectedWorkflow?.wf_name || workflowInfo.workflow_name}|尺寸选择：${selectedSize.width}x${selectedSize.height}|图片提示词：${cleanPrompt}|图片数量：${imageCount}张`;

      const response: ApiResponse<{ task_id: string }> = await api.post(`/workflow/submit`, {
        wf_id: selectedWorkflow?.wf_id || workflowInfo.wf_id,
        wf_type: selectedWorkflow?.wf_type,
        params: processedParams,
        route_slug: routeSlug,
        prompt: prompt,
        width: selectedSize.width,
        height: selectedSize.height,
        count: imageCount,
        task_id: task_id,  // 添加task_id到请求中
        task_name: task_name,  // 添加task_name到请求中
        market_id: workflowInfo?.market_id, // 添加market_id
        ui_params: uiParamsString // 修改为ui_params字段保存界面参数字符串
      });

      if (response.success) {
        message.success('任务提交成功！');
        
        // 刷新历史记录
        if (workflowInfo?.market_id) {
          try {
            // 使用api实例而不是fetch
            const historyResponse: ApiResponse<{ histories: WorkflowMarketHistory[] }> = await api.get(`/workflow-histories/market/${workflowInfo.market_id}/user/${user?.user_id || 'anonymous'}`);
            if (historyResponse.success && historyResponse.data?.histories) {
              setTaskHistory(historyResponse.data.histories);
            }
          } catch (error) {
            console.error('刷新历史记录失败:', error);
          }
        }
      } else {
        message.error(response.message || '任务提交失败');
      }
    } catch (error: any) {
      console.error('提交任务失败:', error);
      console.error('错误详情:', error.response?.data || error.message);
      
      // 显示更详细的错误信息
      if (error.response?.data?.message) {
        message.error(`提交任务失败: ${error.response.data.message}`);
      } else if (error.message) {
        message.error(`提交任务失败: ${error.message}`);
      } else {
        message.error('提交任务失败: 未知错误');
      }
    } finally {
      setSubmitting(false);
    }
  };

  // 清空历史记录
  const handleClearHistory = async () => {
    if (!workflowInfo?.market_id) return;
    
    try {
      const response: ApiResponse = await api.delete(`/workflow-histories/market/${workflowInfo.market_id}/user/${user?.user_id || 'anonymous'}`);
      
      if (response.success) {
        setTaskHistory([]);
        message.success('历史记录已清空');
      } else {
        message.error(response.message || '清空历史记录失败');
      }
    } catch (error) {
      console.error('清空历史记录失败:', error);
      message.error('清空历史记录失败');
    }
  };
  const handleViewTaskDetail = async (taskId: string) => {
    setTaskDetailLoading(true);
    setTaskDetailVisible(true);
    
    try {
      const response: ApiResponse<TaskDetail> = await api.get(`/workflow/task/${taskId}`);
      if (response.success && response.data) {
        setTaskDetail(response.data);
      } else {
        // 显示任务未创建的提示
        setTaskDetail(null);
        message.warning(response.message || '任务还未创建，请等待');
      }
    } catch (error: any) {
      console.error('获取任务详情失败:', error);
      setTaskDetail(null);
      if (error.response?.status === 404) {
        message.warning('任务还未创建，请等待');
      } else {
        message.error('获取任务详情失败');
      }
    } finally {
      setTaskDetailLoading(false);
    }
  };

  // 获取状态标签颜色
  const getStatusColor = (status: string) => {
    switch (status) {
      case '已完成': case 'completed': case 'success': return 'success';
      case '处理中': case 'processing': case '音频生成中': return 'processing';
      case '等待中': case 'pending': case 'waiting': return 'default';
      case '失败': case 'failed': case 'error': return 'error';
      default: return 'default';
    }
  };

  // 获取状态中文显示
  const getStatusText = (status: string) => {
    switch (status) {
      case 'completed': case 'success': return '已完成';
      case 'processing': return '处理中';
      case 'pending': case 'waiting': return '等待中';
      case 'failed': case 'error': return '失败';
      default: return status;
    }
  };

  // 解析并格式化参数字符串
  const formatParams = (inputParams?: string) => {
    if (!inputParams) return [];
    
    const params = inputParams.split('|');
    return params.map((param, index) => {
      // 如果是图片提示词，截取前50个字符
      if (param.startsWith('图片提示词：')) {
        const content = param.substring(6); // 移除"图片提示词："前缀
        return `图片提示词：${content.length > 50 ? content.substring(0, 50) + '...' : content}`;
      }
      return param;
    });
  };

  // 解析返回结果中的URL
  const parseResultUrls = (resultData: any): string[] => {
    if (!resultData) return [];
    
    try {
      let urls: string[] = [];
      if (typeof resultData === 'string') {
        const parsed = JSON.parse(resultData);
        urls = parsed.urls || [];
      } else if (resultData.urls) {
        urls = resultData.urls;
      }
      
      return urls.filter((url: string) => url && url.trim());
    } catch (error) {
      console.error('解析返回结果失败:', error);
      return [];
    }
  };

  if (loading) {
    return <div style={{ padding: '20px', textAlign: 'center' }}>加载中...</div>;
  }

  if (!workflowInfo) {
    return <div style={{ padding: '20px', textAlign: 'center' }}>未找到对应的工作流</div>;
  }

  return (
    <div style={{ padding: '20px', display: 'flex', gap: '20px', height: 'calc(100vh - 40px)' }}>
      {/* 左侧参数配置区域 */}
      <Card 
        title={
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span>{workflowInfo.display_name}</span>
            <Button
              type="text"
              icon={<ArrowLeftOutlined />}
              onClick={() => navigate('/workflow-market')}
              style={{ color: '#1890ff' }}
            >
              返回工作流市场
            </Button>
          </div>
        }
        style={{ flex: 1, height: 'fit-content' }}
      >
        <div style={{ marginBottom: '20px' }}>
          <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>
            工作流选择
          </label>
          <Select
            style={{ width: '100%' }}
            value={selectedWorkflow?.wf_id}
            onChange={(value) => {
              const selected = Array.isArray(workflowOptions) ? workflowOptions.find(option => option.wf_id === value) : null;
              if (selected) setSelectedWorkflow(selected);
            }}
            loading={workflowOptionsLoading}
            placeholder="请选择工作流"
          >
            {Array.isArray(workflowOptions) && workflowOptions.map(option => (
              <Option key={option.wf_id} value={option.wf_id}>
                {option.wf_name}
              </Option>
            ))}
          </Select>
        </div>

        <div style={{ marginBottom: '20px' }}>
          <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>
            尺寸选择
          </label>
          <Select
            style={{ width: '100%' }}
            value={selectedSize.label}
            onChange={(value) => {
              const selected = sizeOptions.find(option => option.label === value);
              if (selected) setSelectedSize(selected);
            }}
          >
            {sizeOptions.map(option => (
              <Option key={option.label} value={option.label}>
                {option.label}
              </Option>
            ))}
          </Select>
        </div>

        <div style={{ marginBottom: '20px' }}>
          <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>
            图片提示词
          </label>
          <TextArea
            rows={4}
            placeholder="请输入您的图片描述，越详细越好..."
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            showCount
            maxLength={500}
          />
        </div>

        <div style={{ marginBottom: '20px' }}>
          <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>
            生成图片数量：
          </label>
          <Select
            style={{ width: '100%' }}
            value={imageCount}
            onChange={setImageCount}
          >
            {[1, 2, 3, 4, 5, 6].map(num => (
              <Option key={num} value={num}>{num}张</Option>
            ))}
          </Select>
        </div>

        <Button
          type="primary"
          size="large"
          icon={<PlayCircleOutlined />}
          onClick={handleSubmit}
          loading={submitting}
          style={{ width: '100%' }}
        >
          开始生图
        </Button>
      </Card>

      {/* 右侧历史记录区域 */}
      <Card 
        title={<><HistoryOutlined /> 历史记录</>}
        extra={
          <Popconfirm
            title="确定要清空所有历史记录吗？"
            description="此操作不可恢复"
            onConfirm={handleClearHistory}
            okText="确定"
            cancelText="取消"
          >
            <Button 
              type="text" 
              danger 
              size="small"
              icon={<DeleteOutlined />}
              disabled={taskHistory.length === 0}
            >
              清空
            </Button>
          </Popconfirm>
        }
        style={{ width: '400px', height: '100%', overflow: 'hidden' }}
        styles={{ body: { height: 'calc(100% - 57px)', overflow: 'auto' } }}
      >
        <List
          loading={historyLoading}
          dataSource={taskHistory}
          locale={{ emptyText: '暂无历史记录' }}
          renderItem={(item) => {
            let parsedParams;
            try {
              parsedParams = item.input_parameters || {};
            } catch (error) {
              parsedParams = {};
            }
            
            return (
              <List.Item
                style={{
                  border: '1px solid #d9d9d9',
                  borderRadius: '8px',
                  backgroundColor: '#f6ffed',
                  marginBottom: '12px',
                  padding: '16px'
                }}
              >
                <div style={{ width: '100%' }}>
                  {/* 任务名称占满一行 */}
                  <div style={{ marginBottom: '12px' }}>
                    <span style={{ fontWeight: 'bold', color: '#1890ff', fontSize: '16px', display: 'block', lineHeight: '1.4' }}>
                      {item.task_name || `任务 ${item.task_id.slice(-8)}`}
                    </span>
                  </div>
                  
                  {/* 任务详细信息 */}
                  <div style={{ marginBottom: '12px' }}>
                    <div style={{ marginBottom: '4px', fontSize: '13px', color: '#666' }}>
                      任务ID: {item.task_id}
                    </div>
                    <div style={{ fontSize: '12px', color: '#666' }}>
                      创建时间: {new Date(item.created_at).toLocaleString()}
                    </div>
                  </div>
                  
                  {/* 底部按钮行 */}
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div>
                      <Tag color="blue">已提交</Tag>
                    </div>
                    <div>
                      <Button
                        type="link"
                        icon={<EyeOutlined />}
                        onClick={() => handleViewTaskDetail(item.task_id)}
                      >
                        查看详情
                      </Button>
                    </div>
                  </div>
                </div>
              </List.Item>
            );
          }}
        />
      </Card>

      {/* 任务详情弹窗 */}
      <Modal
        title="任务执行详情"
        open={taskDetailVisible}
        onCancel={() => setTaskDetailVisible(false)}
        footer={null}
        width={600}
      >
        {taskDetailLoading ? (
          <div style={{ textAlign: 'center', padding: '20px' }}>加载中...</div>
        ) : taskDetail ? (
          <div>
            <div style={{ marginBottom: '16px' }}>
              <strong>任务名：</strong> {taskDetail.task_name || `任务 ${taskDetail.task_id.slice(-8)}`}
            </div>
            <div style={{ marginBottom: '16px' }}>
              <strong>任务ID：</strong> {taskDetail.task_id}
            </div>
            <div style={{ marginBottom: '16px' }}>
              <strong>任务状态：</strong> 
              <Tag color={getStatusColor(taskDetail.status)} style={{ marginLeft: '8px' }}>
                {getStatusText(taskDetail.status)}
              </Tag>
            </div>
            
            <div style={{ marginBottom: '16px' }}>
              <strong>输入参数：</strong>
              <div style={{ marginTop: '8px', paddingLeft: '16px' }}>
                {taskDetail.ui_params ? (
                  formatParams(taskDetail.ui_params).map((param, index) => (
                    <div key={index} style={{ marginBottom: '4px', color: '#666' }}>
                      {param}
                    </div>
                  ))
                ) : (
                  <div style={{ color: '#999', fontStyle: 'italic' }}>暂无输入参数</div>
                )}
              </div>
            </div>
            
            <div style={{ marginBottom: '16px' }}>
              <strong>创建时间：</strong> {new Date(taskDetail.created_at).toLocaleString()}
            </div>
            
            <div style={{ marginBottom: '16px' }}>
              <strong>返回结果：</strong>
              <div style={{ marginTop: '8px', paddingLeft: '16px' }}>
                {taskDetail.result_info ? (
              parseResultUrls(taskDetail.result_info).length > 0 ? (
                parseResultUrls(taskDetail.result_info).map((url, index) => (
                      <div key={index} style={{ marginBottom: '4px' }}>
                        <a href={url} target="_blank" rel="noopener noreferrer" style={{ color: '#1890ff', wordBreak: 'break-all' }}>
                          {url}
                        </a>
                      </div>
                    ))
                  ) : (
                    <div style={{ color: '#999', fontStyle: 'italic' }}>暂无可显示的结果链接</div>
                  )
                ) : (
                  <div style={{ color: '#999', fontStyle: 'italic' }}>暂无返回结果</div>
                )}
              </div>
            </div>
            
          </div>
        ) : (
          <div style={{ textAlign: 'center', padding: '20px' }}>
            <div style={{ fontSize: '16px', color: '#666', marginBottom: '8px' }}>
              任务还未创建，请等待
            </div>
            <div style={{ fontSize: '14px', color: '#999' }}>
              任务正在队列中等待处理，请稍后再查看详情
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default BeautySingImgPage;